/**
 * API functions for pricing configurations.
 * Types sourced from @smithy/o-platform-client where available.
 */

import { axiosInstance } from './instance';
import type { PricingConfig, PricingTier } from './types/smithy';

// Re-export for consumers that import from this module
export type { PricingConfig, PricingTier };

export interface ListPricingConfigsResponse {
  items: PricingConfig[];
  nextToken?: string;
}

/**
 * List pricing configurations for a specific shift using the search endpoint.
 * Only returns ACTIVE configs (inactive configs are hidden from parents).
 */
export async function listPricingConfigs(shiftId: string): Promise<PricingConfig[]> {
  try {
    const query = `@shiftId:"${shiftId}" @status:{ACTIVE}`;
    const response = await axiosInstance.get<{ pricingConfigs: PricingConfig[] }>(
      `/pricing-configs/search`,
      { params: { query } }
    );
    return response.data.pricingConfigs || [];
  } catch (error) {
    console.error('Error fetching pricing configs:', error);
    throw error;
  }
}

/**
 * Get pricing configuration by ID.
 */
export async function getPricingConfig(id: string): Promise<PricingConfig> {
  try {
    const response = await axiosInstance.get<PricingConfig>(`/pricing-configs/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching pricing config:', error);
    throw error;
  }
}

/**
 * Filter pricing tiers based on distance.
 * Returns the tier that matches the given distance range.
 */
export function filterTiersByDistance(
  tiers: PricingTier[],
  distanceKm: number
): PricingTier | null {
  if (!tiers || tiers.length === 0) return null;
  return tiers.find(tier =>
    tier.rangeKm?.from != null &&
    tier.rangeKm?.to != null &&
    distanceKm >= tier.rangeKm.from &&
    distanceKm <= tier.rangeKm.to
  ) ?? null;
}

/**
 * Get applicable pricing configs for given shifts and distance.
 * Extracts all matching pricing tiers from pricingByCapacity.
 */
export async function getApplicablePricingConfigs(
  shiftIds: string[],
  distanceKm: number
): Promise<Array<{ config: PricingConfig; tier: PricingTier; capacity: string }>> {
  try {
    const allConfigs: PricingConfig[] = [];
    for (const shiftId of shiftIds) {
      const configs = await listPricingConfigs(shiftId);
      allConfigs.push(...configs);
    }

    const applicableConfigs: Array<{ config: PricingConfig; tier: PricingTier; capacity: string }> = [];
    for (const config of allConfigs) {
      for (const [capacity, tiers] of Object.entries(config.pricingByCapacity ?? {})) {
        const matchingTier = filterTiersByDistance(tiers, distanceKm);
        if (matchingTier) {
          applicableConfigs.push({ config, tier: matchingTier, capacity });
        }
      }
    }

    return applicableConfigs;
  } catch (error) {
    console.error('Error getting applicable pricing configs:', error);
    throw error;
  }
}
